import { useMemo } from "react"
import { Navigate, useNavigate, useSearch } from "@tanstack/react-router"

import { cn } from "@productlane/lib/src/cn"

import { MetaInfo } from "@/components/meta-info"
import { useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "../../settings-header"
import { ConnectorForm } from "./connector-form"
import { findCustomPreset, findVendorPreset } from "./presets"
import { VendorSetupForm } from "./vendor-setup-form"

export function ConnectorSetupPage() {
	const presetId = useSearch({
		strict: false,
		select: (search) => (search as { preset?: string }).preset,
	})
	const navigate = useNavigate()
	const workspace = useZeroWorkspace()
	const sections = useMemo(
		() => workspace?.sidebarSections ?? [],
		[workspace?.sidebarSections],
	)

	// Header values never sync to the client, so reuse works by reference: the
	// server copies the picked header's stored value.
	const reusableHeaders = useMemo(
		() =>
			sections.flatMap((section) =>
				section.sidebarSectionHeaders.map((header) => ({
					headerId: header.id,
					key: header.key,
					sectionTitle: section.title,
				})),
			),
		[sections],
	)

	const vendor = findVendorPreset(presetId)
	const custom = findCustomPreset(presetId)
	if (!workspace) return null
	if (!vendor && !custom) {
		return <Navigate to="/settings/custom-connectors" replace />
	}

	const openConnector = (id: string) =>
		void navigate({
			to: "/settings/custom-connectors/$connectorId",
			params: { connectorId: id },
		})

	const preset = vendor ?? custom
	const title = vendor ? `Connect ${vendor.title}` : (custom?.title ?? "")
	return (
		<>
			<MetaInfo title={title} />
			<SettingsHeader
				title={title}
				titlePrefix={
					preset ? (
						<span
							className={cn(
								"flex h-8 w-8 flex-none items-center justify-center rounded-md bg-white text-primary ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/10",
								vendor?.logoFillsTile ? "overflow-hidden p-0" : "p-1.5",
							)}
						>
							<preset.Logo className="h-full w-full flex-none" />
						</span>
					) : null
				}
				subtitle={
					vendor
						? `${vendor.summary} You pick which actions the agent may use next.`
						: custom?.type === "MCP"
							? "Paste the server's URL and how it signs in. You pick which actions the agent may use next."
							: "Productlane calls your endpoint for the customer in a thread and shows what it returns."
				}
			/>
			{vendor ? (
				<VendorSetupForm
					preset={vendor}
					nextOrder={sections.length}
					onSaved={openConnector}
				/>
			) : custom ? (
				<ConnectorForm
					type={custom.type}
					data={null}
					nextOrder={sections.length}
					reusableHeaders={reusableHeaders}
					onSaved={openConnector}
				/>
			) : null}
		</>
	)
}
